import paper from "paper";
import { ToolBase } from "./ToolBase";
import type { PointerSample } from "../canvas/PointerInput";
import { newId } from "../model/ids";
import { findPiece, withDecoration } from "../model/projectOps";
import { pathFromBuffer, setPathPoints } from "../canvas/Scene";
import { cssVar } from "../colors";
import { nextStampDecorationName, type FillDecoration } from "../model/Project";

/**
 * Click on a piece to fill its full area with the active stamp, or
 * drag to define a custom region (lasso) inside a piece.
 */
export class FillTool extends ToolBase {
  id = "fill";
  label = "Fill";
  // "F" now belongs to the Region (leather fill) tool; Fill has no shortcut.
  cursor = "crosshair";

  private lasso: { x: number; y: number }[] = [];
  private preview: paper.Path | null = null;
  private dragged = false;

  override onDown(_s: PointerSample, p: { x: number; y: number }) {
    this.lasso = [{ x: p.x, y: p.y }];
    this.dragged = false;
    this.startPreview();
  }

  override onMove(_s: PointerSample, p: { x: number; y: number }) {
    const last = this.lasso[this.lasso.length - 1];
    if (!last || Math.hypot(p.x - last.x, p.y - last.y) > 1.5) {
      this.lasso.push({ x: p.x, y: p.y });
      this.dragged = true;
      this.updatePreview();
    }
  }

  override onUp(_s: PointerSample, p: { x: number; y: number }) {
    const stampId = this.ctx.settings.activeStampId;
    if (!stampId) {
      this.cleanup();
      return;
    }
    const meta = this.ctx.library.get(stampId);
    if (!meta || !meta.tile) {
      this.cleanup();
      console.warn("Active stamp is not tileable; aborting fill.");
      return;
    }

    let regionData: string;
    let pieceId: string | undefined;
    const scope = this.ctx.scene.getPaperScope();

    if (this.dragged && this.lasso.length >= 3) {
      regionData = pathFromBuffer(this.lasso) + " Z";
      const tmp = new scope.CompoundPath(regionData);
      const center = tmp.bounds.center;
      tmp.remove();
      const hit = this.ctx.scene.hitTest(center);
      pieceId =
        hit.pieceId ??
        this.ctx.scene.getSelection().pieceId ??
        this.ctx.history.state.pieces[0]?.id;
    } else {
      const hit = this.ctx.scene.hitTest(new scope.Point(p.x, p.y));
      pieceId =
        hit.pieceId ??
        this.ctx.scene.getSelection().pieceId ??
        this.ctx.history.state.pieces[0]?.id;
      const piece = pieceId ? findPiece(this.ctx.history.state, pieceId) : null;
      if (!piece) {
        this.cleanup();
        return;
      }
      regionData = piece.shapePathData;
    }
    this.cleanup();
    if (!pieceId) return;

    const targetPiece = this.ctx.history.state.pieces.find(
      (p) => p.id === pieceId,
    );
    const baseName = `${meta.name} (fill)`;
    const name = targetPiece
      ? nextStampDecorationName(targetPiece, baseName)
      : baseName;
    const dec: FillDecoration = {
      id: newId("dec"),
      kind: "fill",
      name,
      visible: true,
      depth: meta.deboss ? -0.6 : 0.6,
      stampId,
      regionPathData: regionData,
      rotation: this.ctx.settings.fillRotation,
      scale: 1,
      edgeMatch: true,
    };
    this.ctx.commit("Fill region", (proj) =>
      withDecoration(proj, pieceId!, dec),
    );
  }

  override onCancel() {
    this.cleanup();
  }

  private startPreview() {
    const scope = this.ctx.scene.getPaperScope();
    this.preview = new scope.Path();
    this.preview.strokeColor = new scope.Color(cssVar("--preview-fill"));
    this.preview.strokeWidth = 1;
    this.preview.dashArray = [3, 3];
    this.preview.fillColor = null;
    this.preview.data = { ephemeral: true };
  }

  private updatePreview() {
    if (!this.preview) return;
    const scope = this.ctx.scene.getPaperScope();
    this.preview.removeSegments();
    setPathPoints(
      this.preview,
      this.lasso.map((p) => new scope.Point(p.x, p.y)),
    );
  }

  private cleanup() {
    if (this.preview) {
      this.preview.remove();
      this.preview = null;
    }
    this.lasso = [];
    this.dragged = false;
  }
}
